import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { Tabs } from '@hub/common/Tabs/Tabs';
import { ChemicalResistance } from '@igus/material-domain';
import { BearingHubHelper } from '@hub/libs/bearing-hub.helper';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { IbhFilterKeys } from '@hub/libs/constants';
import { useFreeFormHubInputValidator } from '@hub/free-form-hub/free-form-hub-result/product-filters/hooks/inputValidator';
import { FilterType } from '@hub/free-form-hub/free-form-hub-result/product-filters/types';
import { useFreeFormHubFilterState } from '@hub/free-form-hub/free-form-hub-result/product-filters/hooks/useUrlFilterState';
import { Dialog } from '@hub/common/Dialog/Dialog';
import { ffhEventCategory, TrackedFfhEventActions } from '@hub/free-form-hub/tracking.config';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import type { IbhMaterialResponseDtoProcedureEnum } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { Material } from '@igus/iglidur-free-form-hub-interfaces';
import { useMediaQuery } from '@hub/hooks/useMediaQuery';

export interface CompareMaterialsModalProps {
  activeModal: boolean;
  materials: Material[];
  setActiveModal: (value: boolean) => void;
}

type TableData = { key: keyof Omit<Material, 'chemicals'> | string; translationKey: string }[];

enum TableDataType {
  CHARACTERISTIC = 'characteristic',
  CHEMICAL = 'chemical',
}

const presentedMaterialCharacteristic: TableData = [
  {
    key: 'underWaterCompliant',
    translationKey: 'UNDER_WATER_COMPLIANT',
  },
  {
    key: 'foodContactFDACompliant',
    translationKey: 'FOOD_CONTACT_FDA_COMPLIANT',
  },
  {
    key: 'foodContactEUCompliant',
    translationKey: 'FOOD_CONTACT_EU_COMPLIANT',
  },
  {
    key: 'electricallyConductive',
    translationKey: 'ELECTRICALLY_CONDUCTIVE',
  },
  {
    key: 'PTFEFree',
    translationKey: 'PTFE_FREE',
  },
  {
    key: 'PFASFree',
    translationKey: 'PFAS_FREE',
  },
  {
    key: 'dirtResistant',
    translationKey: 'DIRT_RESISTANT',
  },
  {
    key: 'mouldResistant',
    translationKey: 'MOULD_RESISTANT',
  },
  {
    key: 'highChemicalResistance',
    translationKey: 'HIGH_CHEMICAL_RESISTANCE',
  },
  {
    key: 'lowWaterAbsorption',
    translationKey: 'LOW_WATER_ABSORPTION',
  },
  {
    key: 'heatedMold',
    translationKey: 'HEATED_MOLD',
  },
  {
    key: 'injectionMoldingCalculability',
    translationKey: 'INJECTION_MOLDING_CALCULATABILITY',
  },
];

const presentedChemicals: TableData = [
  { key: 'acetaldehydeAq40pct', translationKey: 'ACETALDEHYDE_AQ_40PCT' },
  { key: 'acetamideAq50pct', translationKey: 'ACETAMIDE_AQ_50PCT' },
  { key: 'acetone', translationKey: 'ACETONE' },
  { key: 'acetylChloride', translationKey: 'ACETYL_CHLORIDE' },
  { key: 'acrylnitrile', translationKey: 'ACRYLNITRILE' },
  { key: 'allylAlcohol', translationKey: 'ALLYL_ALCOHOL' },
  { key: 'aluminiumChlorideAq10pct', translationKey: 'ALUMINIUM_CHLORIDE_AQ_10PCT' },
  { key: 'aluminiumCleaner', translationKey: 'ALUMINIUM_CLEANER' },
  { key: 'aluminiumSaltsMineralAcids20pct', translationKey: 'ALUMINIUM_SALTS_MINERAL_ACIDS_20PCT' },
  { key: 'aluminiumSulphateAq10pct', translationKey: 'ALUMINIUM_SULPHATE_AQ_10PCT' },
  { key: 'formicAcidAq2pct', translationKey: 'FORMIC_ACID_AQ_2PCT' },
  { key: 'formicAcid10pct', translationKey: 'FORMIC_ACID_10PCT' },
  { key: 'formicAcid90pct', translationKey: 'FORMIC_ACID_90PCT' },
  { key: 'ammoniumCarbonateAq10pct', translationKey: 'AMMONIUM_CARBONATE_AQ_10PCT' },
  { key: 'ammoniumChlorideAq10pct', translationKey: 'AMMONIUM_CHLORIDE_AQ_10PCT' },
  { key: 'amylAcetate100pct', translationKey: 'AMYL_ACETATE_100PCT' },
  { key: 'amylAlcohol', translationKey: 'AMYL_ALCOHOL' },
  { key: 'anilineSaturatedSolution', translationKey: 'ANILINE_SATURATED_SOLUTION' },
  { key: 'aromaticCompounds', translationKey: 'AROMATIC_COMPOUNDS' },
  { key: 'bariumChlorideAq10pct', translationKey: 'BARIUM_CHLORIDE_AQ_10PCT' },
  { key: 'bariumSaltsMineralAcids', translationKey: 'BARIUM_SALTS_MINERAL_ACIDS' },
  { key: 'bariumSulphateAq10pct', translationKey: 'BARIUM_SULPHATE_AQ_10PCT' },
  { key: 'benzaldehyde', translationKey: 'BENZALDEHYDE' },
  { key: 'benzoicAcidAq20pct', translationKey: 'BENZOIC_ACID_AQ_20PCT' },
  { key: 'benzylAlcohol', translationKey: 'BENZYL_ALCOHOL' },
  { key: 'biphenyl', translationKey: 'BIPHENYL' },
  { key: 'bitumenDin51567', translationKey: 'BITUMEN_DIN51567' },
  { key: 'leadAcetateAq10pct', translationKey: 'LEAD_ACETATE_AQ_10PCT' },
  { key: 'bleachingSolution', translationKey: 'BLEACHING_SOLUTION' },
  { key: 'bleachingSolutionAq10pct', translationKey: 'BLEACHING_SOLUTION_AQ_10PCT' },
  { key: 'leadStearate', translationKey: 'LEAD_STEARATE' },
  { key: 'drillingOils', translationKey: 'DRILLING_OILS' },
  { key: 'boricAcidAq10pct', translationKey: 'BORIC_ACID_AQ_10PCT' },
  { key: 'brandyVinegar', translationKey: 'BRANDY_VINEGAR' },
  { key: 'pyrocatecholAq6pct', translationKey: 'PYROCATECHOL_AQ_6PCT' },
  { key: 'pyruvicAcidAq10pct', translationKey: 'PYRUVIC_ACID_AQ_10PCT' },
  { key: 'bromineAq25pct', translationKey: 'BROMINE_AQ_25PCT' },
  { key: 'bromineVapours', translationKey: 'BROMINE_VAPOURS' },
  { key: 'bromhydricAcidAq10pct', translationKey: 'BROMHYDRIC_ACID_AQ_10PCT' },
  { key: 'butyricAcid', translationKey: 'BUTYRIC_ACID' },
  { key: 'butylAcetate', translationKey: 'BUTYL_ACETATE' },
  { key: 'butylGlycolate', translationKey: 'BUTYL_GLYCOLATE' },
  { key: 'butylGlycol', translationKey: 'BUTYL_GLYCOL' },
  { key: 'butylPhthalate', translationKey: 'BUTYL_PHTHALATE' },
  { key: 'calciumChlorideSaturatedSolution', translationKey: 'CALCIUM_CHLORIDE_SATURATED_SOLUTION' },
  { key: 'calciumHydroxideAq', translationKey: 'CALCIUM_HYDROXIDE_AQ' },
  { key: 'calciumHypochlorite', translationKey: 'CALCIUM_HYPOCHLORITE' },
  { key: 'camphor', translationKey: 'CAMPHOR' },
  { key: 'cellulosePaints', translationKey: 'CELLULOSE_PAINTS' },
  { key: 'chlorine', translationKey: 'CHLORINE' },
  { key: 'chloramine', translationKey: 'CHLORAMINE' },
  { key: 'chlorobromomethane98pct', translationKey: 'CHLOROBROMOMETHANE_98PCT' },
  { key: 'chloroaceticAcidAq10pct', translationKey: 'CHLOROACETIC_ACID_AQ_10PCT' },
  { key: 'chlorethanal', translationKey: 'CHLORETHANAL' },
  { key: 'chlorineGas', translationKey: 'CHLORINE_GAS' },
  { key: 'chloroform', translationKey: 'CHLOROFORM' },
  { key: 'chlorsulphonicAcidAq', translationKey: 'CHLORSULPHONIC_ACID_AQ' },
  { key: 'chlorinatedWaterSaturatedSolution', translationKey: 'CHLORINATED_WATER_SATURATED_SOLUTION' },
  { key: 'hydrogenChlorideGas', translationKey: 'HYDROGEN_CHLORIDE_GAS' },
  { key: 'chromicAcidAq1pct', translationKey: 'CHROMIC_ACID_AQ_1PCT' },
  { key: 'chromicAcidAq10pct', translationKey: 'CHROMIC_ACID_AQ_10PCT' },
  { key: 'cyclohexane', translationKey: 'CYCLOHEXANE' },
  { key: 'decahydronaphthalene', translationKey: 'DECAHYDRONAPHTHALENE' },
  { key: 'dibutylphthalate', translationKey: 'DIBUTYLPHTHALATE' },
  { key: 'dichlorobenzene', translationKey: 'DICHLOROBENZENE' },
  { key: 'dichloroethene', translationKey: 'DICHLOROETHENE' },
  { key: 'dichlorethylene', translationKey: 'DICHLORETHYLENE' },
  { key: 'diethylEther', translationKey: 'DIETHYL_ETHER' },
  { key: 'dimethylformamide', translationKey: 'DIMETHYLFORMAMIDE' },
  { key: 'dioctylphthalate', translationKey: 'DIOCTYLPHTHALATE' },
  { key: 'dioxane', translationKey: 'DIOXANE' },
  { key: 'inertGasesArgonHeliumNeon', translationKey: 'INERT_GASES_ARGON_HELIUM_NEON' },
  { key: 'ferricChlorideSaturatedSolution', translationKey: 'FERRIC_CHLORIDE_SATURATED_SOLUTION' },
  { key: 'ferricChloride2_5pct', translationKey: 'FERRIC_CHLORIDE_2.5PCT' },
  { key: 'ferricChloride5pct', translationKey: 'FERRIC_CHLORIDE_5PCT' },
  { key: 'ironIiiChlorideAqNeutral10pct', translationKey: 'IRON_III-CHLORIDE_AQ_NEUTRAL_10PCT' },
  { key: 'ironIiiChlorideAqNeutralAcidic10pct', translationKey: 'IRON_III-CHLORIDE_AQ_NEUTRAL_ACIDIC_10PCT' },
  { key: 'anodicSolutionsHno330pctH2so410pct', translationKey: 'ANODIC_SOLUTIONS_HNO3-30PCT_H2SO4-10PCT' },
  { key: 'aceticAcid2pct', translationKey: 'ACETIC_ACID_2PCT' },
  { key: 'aceticAcid10pct', translationKey: 'ACETIC_ACID_10PCT' },
  { key: 'aceticAcid90pct', translationKey: 'ACETIC_ACID_90PCT' },
  { key: 'ethanalAq40pct', translationKey: 'ETHANAL_AQ_40PCT' },
  { key: 'ethanolAq96pct', translationKey: 'ETHANOL_AQ_96PCT' },
  { key: 'ethylAcetate', translationKey: 'ETHYL_ACETATE' },
  { key: 'ethylene', translationKey: 'ETHYLENE' },
  { key: 'ethylChloride', translationKey: 'ETHYL_CHLORIDE' },
  { key: 'ethyleneDiamine12EthyleneDiamine', translationKey: 'ETHYLENE_DIAMINE_1.2-ETHYLENE_DIAMINE' },
  { key: 'ethyleneGlycolAq95pct', translationKey: 'ETHYLENE_GLYCOL_AQ_95PCT' },
  { key: 'ethyleneOxide12Epoxyethane', translationKey: 'ETHYLENE_OXIDE_1.2-EPOXYETHANE' },
  { key: 'greasesCookingFat', translationKey: 'GREASES_COOKING_FAT' },
  { key: 'fluorinatedHydrocarbons', translationKey: 'FLUORINATED_HYDROCARBONS' },
  { key: 'hydrofluoricAcidAq4pct', translationKey: 'HYDROFLUORIC_ACID_AQ_4PCT' },
  { key: 'formaldehydeAq30pct', translationKey: 'FORMALDEHYDE_AQ_30PCT' },
  { key: 'formamide', translationKey: 'FORMAMIDE' },
  { key: 'fruitJuices', translationKey: 'FRUIT_JUICES' },
  { key: 'furfurol', translationKey: 'FURFUROL' },
  { key: 'glycerine', translationKey: 'GLYCERINE' },
  { key: 'uricAcidAq10pct', translationKey: 'URIC_ACID_AQ_10PCT' },
  { key: 'urea', translationKey: 'UREA' },
  { key: 'heptane', translationKey: 'HEPTANE' },
  { key: 'hexachlorobenzene', translationKey: 'HEXACHLOROBENZENE' },
  { key: 'hexachloroethane', translationKey: 'HEXACHLOROETHANE' },
  { key: 'hexamethylphosphoricAcidTriamide', translationKey: 'HEXAMETHYLPHOSPHORIC_ACID_TRIAMIDE' },
  { key: 'hexane', translationKey: 'HEXANE' },
  { key: 'humicAcids', translationKey: 'HUMIC_ACIDS' },
  { key: 'hydrochinoneAq5pct', translationKey: 'HYDROCHINONE_AQ_5PCT' },
  { key: 'isooctane80pct', translationKey: 'ISOOCTANE_80PCT' },
  { key: 'isopropylAlcoholIsopropanol', translationKey: 'ISOPROPYL_ALCOHOL_ISOPROPANOL' },
  { key: 'iodineTincture3pct', translationKey: 'IODINE_TINCTURE_3PCT' },
  { key: 'causticPotash10pct', translationKey: 'CAUSTIC_POTASH_10PCT' },
  { key: 'causticPotash20pct', translationKey: 'CAUSTIC_POTASH_20PCT' },
  { key: 'causticPotash50pct', translationKey: 'CAUSTIC_POTASH_50PCT' },
  { key: 'potassiumBromideAq10pct', translationKey: 'POTASSIUM_BROMIDE_AQ_10PCT' },
  { key: 'potassiumCarbonateAq60pct', translationKey: 'POTASSIUM_CARBONATE_AQ_60PCT' },
  { key: 'potassiumChlorideAq10pct', translationKey: 'POTASSIUM_CHLORIDE_AQ_10PCT' },
  { key: 'potassiumChlorideAq90pct', translationKey: 'POTASSIUM_CHLORIDE_AQ_90PCT' },
  { key: 'potassiumDichromateAq5pct', translationKey: 'POTASSIUM_DICHROMATE_AQ_5PCT' },
  { key: 'potassiumNitrateAq10pct', translationKey: 'POTASSIUM_NITRATE_AQ_10PCT' },
  { key: 'potassiumPermanganateAq1pct', translationKey: 'POTASSIUM_PERMANGANATE_AQ_1PCT' },
  { key: 'potassiumSulphateSaturatedSolution', translationKey: 'POTASSIUM_SULPHATE_SATURATED_SOLUTION' },
  { key: 'casein', translationKey: 'CASEIN' },
  { key: 'causticPotashLyeAq40pct', translationKey: 'CAUSTIC_POTASH_LYE_AQ_40PCT' },
  { key: 'causticSodiumBicarbAq50pct', translationKey: 'CAUSTIC_SODIUM_BICARB_AQ_50PCT' },
  { key: 'ketonesAliphatic', translationKey: 'KETONES_ALIPHATIC' },
  { key: 'hydrofluorosilicicAcidAq30pct', translationKey: 'HYDROFLUOROSILICIC_ACID_AQ_30PCT' },
  { key: 'cobaltSaltsAq', translationKey: 'COBALT_SALTS_AQ' },
  { key: 'carbonDioxideGas', translationKey: 'CARBON_DIOXIDE_GAS' },
  { key: 'carbonicAcidAmmoniaAq10pct', translationKey: 'CARBONIC_ACID_AMMONIA_AQ_10PCT' },
  { key: 'carbonBisulphide', translationKey: 'CARBON_BISULPHIDE' },
  { key: 'aquaRegiaHciHno375_50Vol', translationKey: 'AQUA_REGIA_HCI_HNO3_75_50_VOL' },
  { key: 'cresol', translationKey: 'CRESOL' },
  { key: 'copperSulphateSaturatedSolution', translationKey: 'COPPER_SULPHATE_SATURATED_SOLUTION' },
  { key: 'copperSulphate0_5pct', translationKey: 'COPPER_SULPHATE_0.5PCT' },
  { key: 'linseedOil', translationKey: 'LINSEED_OIL' },
  { key: 'lithiumBromideChlorideSaltsAq50pct', translationKey: 'LITHIUM_BROMIDE_-CHLORIDE_-SALTS_AQ_50PCT' },
  { key: 'lithiumChlorideInAlcohol20pct', translationKey: 'LITHIUM_CHLORIDE_IN_ALCOHOL_20PCT' },
  { key: 'solderingFluid', translationKey: 'SOLDERING_FLUID' },
  { key: 'airLiquid', translationKey: 'AIR_LIQUID' },
  { key: 'magnesiumChlorideAq10pct', translationKey: 'MAGNESIUM_CHLORIDE_AQ_10PCT' },
  { key: 'magnesiumHydroxideAq', translationKey: 'MAGNESIUM_HYDROXIDE_AQ' },
  { key: 'maleicAcidConcentratedSolution', translationKey: 'MALEIC_ACID_CONCENTRATED_SOLUTION' },
  { key: 'maleicAcidAq10pct', translationKey: 'MALEIC_ACID_AQ_10PCT' },
  { key: 'malt', translationKey: 'MALT' },
  { key: 'manganeseSulphateAq10pct', translationKey: 'MANGANESE_SULPHATE_AQ_10PCT' },
  { key: 'seawater', translationKey: 'SEAWATER' },
  { key: 'molasses', translationKey: 'MOLASSES' },
  { key: 'methane', translationKey: 'METHANE' },
  { key: 'methanol20pctCacl2OrLicl', translationKey: 'METHANOL_20PCT_CACL2_OR_LICL' },
  { key: 'methylAcetate', translationKey: 'METHYL_ACETATE' },
  { key: 'methylamine', translationKey: 'METHYLAMINE' },
  { key: 'methyleneChloride', translationKey: 'METHYLENE_CHLORIDE' },
  { key: 'methylEthylKetone', translationKey: 'METHYL_ETHYL_KETONE' },
  { key: 'milk', translationKey: 'MILK' },
  { key: 'lacticAcid10pct', translationKey: 'LACTIC_ACID_10PCT' },
  { key: 'lacticAcid90pct', translationKey: 'LACTIC_ACID_90PCT' },
  { key: 'molykoteLubricationGrease', translationKey: 'MOLYKOTE-LUBRICATION_GREASE' },
  { key: 'mortarCementLime', translationKey: 'MORTAR_CEMENT_LIME' },
  { key: 'naphthalene', translationKey: 'NAPHTHALENE' },
  { key: 'naphthalenesulfonicAcid', translationKey: 'NAPHTHALENESULFONIC_ACID' },
  { key: 'sodiumAcetateAq10pct', translationKey: 'SODIUM_ACETATE_AQ_10PCT' },
  { key: 'sodiumBisulphiteAq10pct', translationKey: 'SODIUM_BISULPHITE_AQ_10PCT' },
  { key: 'sodiumBromideAq10pct', translationKey: 'SODIUM_BROMIDE_AQ_10PCT' },
  { key: 'sodiumCarbonate5pct', translationKey: 'SODIUM_CARBONATE_5PCT' },
  { key: 'sodiumCarbonateAqueous21_5pct', translationKey: 'SODIUM_CARBONATE_AQUEOUS_21.5PCT' },
  { key: 'sodiumCarbonateAq50pct', translationKey: 'SODIUM_CARBONATE_AQ_50PCT' },
  { key: 'sodiumChlorateAq10pct', translationKey: 'SODIUM_CHLORATE_AQ_10PCT' },
  { key: 'sodiumChlorideSaturatedSolution', translationKey: 'SODIUM_CHLORIDE_SATURATED_SOLUTION' },
  { key: 'sodiumDichromateAq10pct', translationKey: 'SODIUM_DICHROMATE_AQ_10PCT' },
  { key: 'sodiumDodecylBenzenesulfonate', translationKey: 'SODIUM_DODECYL_BENZENESULFONATE' },
  { key: 'sodiumHypochloriteAq10pct', translationKey: 'SODIUM_HYPOCHLORITE_AQ_10PCT' },
  { key: 'sodiumHypophosphiteAqueous10pct', translationKey: 'SODIUM_HYPOPHOSPHITE_AQUEOUS_10PCT' },
  { key: 'sodiumNitrateAq10pct', translationKey: 'SODIUM_NITRATE_AQ_10PCT' },
  { key: 'sodiumNitrilotriacetatAqueous10pct', translationKey: 'SODIUM_NITRILOTRIACETATE_AQUEOUS_10PCT' },
  { key: 'sodiumOleate', translationKey: 'SODIUM_OLEATE' },
  { key: 'sodiumSalts10pct', translationKey: 'SODIUM_SALTS_10PCT' },
  { key: 'sodiumSulphate10pct', translationKey: 'SODIUM_SULPHATE_10PCT' },
  { key: 'sodiumSulphiteNeutral2pct', translationKey: 'SODIUM_SULPHITE_NEUTRAL_2PCT' },
  { key: 'sodiumThiosulphate10pct', translationKey: 'SODIUM_THIOSULPHATE_10PCT' },
  { key: 'sodaLyeAq10pct', translationKey: 'SODA_LYE_AQ_10PCT' },
  { key: 'sodaLyeAq50pct', translationKey: 'SODA_LYE_AQ_50PCT' },
  { key: 'nickelSaltsAq10pct', translationKey: 'NICKEL_SALTS_AQ_10PCT' },
  { key: 'nitriloaceticAcid', translationKey: 'NITRILOACETIC_ACID' },
  { key: 'nitrobenzene', translationKey: 'NITROBENZENE' },
  { key: 'nitrousGases', translationKey: 'NITROUS_GASES' },
  { key: 'nitroPaintHazardClassAI', translationKey: 'NITRO_PAINT_HAZARD_CLASS_A_I' },
  { key: 'nitroPaintHazardClassAIi', translationKey: 'NITRO_PAINT_HAZARD_CLASS_A_II' },
  { key: 'nitromethane', translationKey: 'NITROMETHANE' },
  { key: 'nitrotoluene', translationKey: 'NITROTOLUENE' },
  { key: 'octane', translationKey: 'OCTANE' },
  { key: 'oilCooking', translationKey: 'OIL_COOKING' },
  { key: 'oleicAcid', translationKey: 'OLEIC_ACID' },
  { key: 'oxalicAcidAq10pct', translationKey: 'OXALIC_ACID_AQ_10PCT' },
  { key: 'ozone', translationKey: 'OZONE' },
  { key: 'palmiticAcid', translationKey: 'PALMITIC_ACID' },
  { key: 'paraffin', translationKey: 'PARAFFIN' },
  { key: 'paraffinOil', translationKey: 'PARAFFIN_OIL' },
  { key: 'perfume', translationKey: 'PERFUME' },
  { key: 'perchlorethene', translationKey: 'PERCHLORETHENE' },
  { key: 'perchloricAcid10pct', translationKey: 'PERCHLORIC_ACID_10PCT' },
  { key: 'phenolSpirituou70pct', translationKey: 'PHENOL_SPIRITUOUS_70PCT' },
  { key: 'phenolAq6pct', translationKey: 'PHENOL_AQ_6PCT' },
  { key: 'phenolAq88pct', translationKey: 'PHENOL_AQ_88PCT' },
  { key: 'phosphoricAcidAq0_3pct', translationKey: 'PHOSPHORIC_ACID_AQ_0.3PCT' },
  { key: 'phosphoricAcidAq3pct', translationKey: 'PHOSPHORIC_ACID_AQ_3PCT' },
  { key: 'phosphoricAcidAq10pct', translationKey: 'PHOSPHORIC_ACID_AQ_10PCT' },
  { key: 'phthalicAcidSaturatedSolution', translationKey: 'PHTHALIC_ACID_SATURATED_SOLUTION' },
  { key: 'polyesterResinsWithStyrene', translationKey: 'POLYESTER_RESINS_WITH_STYRENE' },
  { key: 'propanePropene', translationKey: 'PROPANE_PROPENE' },
  { key: 'propenoicAcid', translationKey: 'PROPENOIC_ACID' },
  { key: 'pyridine', translationKey: 'PYRIDINE' },
  { key: 'mercury', translationKey: 'MERCURY' },
  { key: 'mercuryChloride6pct', translationKey: 'MERCURY_CHLORIDE_6PCT' },
  { key: 'resorcinol1_3Dihydroxybenzene50pct', translationKey: 'RESORCINOL_1.3-DIHYDROXYBENZENE_50PCT' },
  { key: 'salicylicAcid', translationKey: 'SALICYLIC_ACID' },
  { key: 'nitricAcidAq2pct', translationKey: 'NITRIC_ACID_AQ_2PCT' },
  { key: 'nitricAcidAq5pct', translationKey: 'NITRIC_ACID_AQ_5PCT' },
  { key: 'hydrochloricAcid2pct', translationKey: 'HYDROCHLORIC_ACID_2PCT' },
  { key: 'hydrochloricAcid10pct', translationKey: 'HYDROCHLORIC_ACID_10PCT' },
  { key: 'oxygenGas23cWithoutPressure', translationKey: 'OXYGEN_GAS_23C_WITHOUT_PRESSURE' },
  { key: 'lubricatingOilMineral', translationKey: 'LUBRICATING_OIL_MINERAL' },
  { key: 'lubricatingOilSynthetic', translationKey: 'LUBRICATING_OIL_SYNTHETIC' },
  { key: 'sulphur', translationKey: 'SULPHUR' },
  { key: 'sulphuricAcid2pct', translationKey: 'SULPHURIC_ACID_2PCT' },
  { key: 'sulphuricAcid10pct', translationKey: 'SULPHURIC_ACID_10PCT' },
  { key: 'sulphuricAcidConcentrated98pct', translationKey: 'SULPHURIC_ACID_CONCENTRATED_98PCT' },
  { key: 'hydrogenSulphideDry', translationKey: 'HYDROGEN_SULPHIDE_DRY' },
  { key: 'hydrogenSulphideAq', translationKey: 'HYDROGEN_SULPHIDE_AQ' },
  { key: 'soapSuds', translationKey: 'SOAP_SUDS' },
  { key: 'silverNitrate', translationKey: 'SILVER_NITRATE' },
  { key: 'siliconeOils', translationKey: 'SILICONE_OILS' },
  { key: 'sodaSolution10pct', translationKey: 'SODA_SOLUTION_10PCT' },
  { key: 'edibleFat100pct', translationKey: 'EDIBLE_FAT_100PCT' },
  { key: 'edibleOils', translationKey: 'EDIBLE_OILS' },
  { key: 'nitrogenOxidesDry', translationKey: 'NITROGEN_OXIDES_DRY' },
  { key: 'inkPrintingInk', translationKey: 'INK_PRINTING_INK' },
  { key: 'transformerOil', translationKey: 'TRANSFORMER_OIL' },
  { key: 'trichloroaceticAcidAq50pct', translationKey: 'TRICHLOROACETIC_ACID_AQ_50PCT' },
  { key: 'trichloroethane', translationKey: 'TRICHLOROETHANE' },
  { key: 'trichlorethylene', translationKey: 'TRICHLORETHYLENE' },
  { key: 'triethanolamine90pct', translationKey: 'TRIETHANOLAMINE_90PCT' },
  { key: 'trisodiumPhosphate90pct', translationKey: 'TRISODIUM_PHOSPHATE_90PCT' },
  { key: 'indiaInk', translationKey: 'INDIA_INK' },
  { key: 'uraniumFluorides', translationKey: 'URANIUM_FLUORIDES' },
  { key: 'urine', translationKey: 'URINE' },
  { key: 'violetOil', translationKey: 'VIOLET_OIL' },
  { key: 'waxMelted', translationKey: 'WAX_MELTED' },
  {
    key: 'washMachineCleanerPhosphoricAndNitricAcids',
    translationKey: 'WASH_MACHINE_CLEANER_PHOSPHORIC_AND_NITRIC_ACIDS',
  },
  { key: 'steam', translationKey: 'STEAM' },
  { key: 'waterGlassSodiumSilicate', translationKey: 'WATER_GLASS_SODIUM_SILICATE' },
  { key: 'hydrogenPeroxide0_5pct', translationKey: 'HYDROGEN_PEROXIDE_0.5PCT' },
  { key: 'hydrogenPeroxide30pct', translationKey: 'HYDROGEN_PEROXIDE_30PCT' },
  { key: 'tartaricAcid', translationKey: 'TARTARIC_ACID' },
  { key: 'whiteSpirit', translationKey: 'WHITE_SPIRIT' },
  { key: 'zincChlorideAq10pct', translationKey: 'ZINC_CHLORIDE_AQ_10PCT' },
  { key: 'zincOxide', translationKey: 'ZINC_OXIDE' },
  { key: 'zincSulphateAq10pct', translationKey: 'ZINC_SULPHATE_AQ_10PCT' },
  { key: 'citricAcidConcentratedSolution', translationKey: 'CITRIC_ACID_CONCENTRATED_SOLUTION' },
  { key: 'citricAcidAq10pct', translationKey: 'CITRIC_ACID_AQ_10PCT' },
  { key: 'citrusFruits', translationKey: 'CITRUS_FRUITS' },
];

const chemicalsPerPage = 20;

export const CompareMaterialsModal = ({ activeModal, setActiveModal, materials }: CompareMaterialsModalProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { t } = useTranslation(['category/productFilter', 'common']);
  const [maxTableHeight, setMaxTableHeight] = useState<number>();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const selectValidator = useFreeFormHubInputValidator(FilterType.AUTOCOMPLETE);
  const [_, setValue] = useFreeFormHubFilterState(IbhFilterKeys.MATERIAL, selectValidator);
  const [loadedChemicals, setLoadedChemicals] = useState<TableData>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { CustomEvent } = usePiwikPro();
  const isMobile = useMediaQuery('(max-width: 425px)');

  useEffect(() => {
    if (isMobile) {
      setIsFullScreen(true);
    }
  }, []);

  const onResize = () => {
    const tabsPane = window.document.querySelectorAll('[data-testid="tabs-pane"]')?.[0];
    const tabsContainer = window.document.querySelectorAll('[data-testid="dialog-container"]')?.[0];
    if (tabsPane && tabsContainer) {
      setMaxTableHeight(tabsContainer.clientHeight - tabsPane.getBoundingClientRect().top - 30);
    }
  };

  const canRenderDot = (material: Material, key: keyof Omit<Material, 'chemicals'>): boolean =>
    material[key as keyof Material] as boolean;

  const renderChemicalResistance = (material: Material, index: number) => {
    switch (material.chemicals[index]?.resistance) {
      case ChemicalResistance.resistant:
        return (
          <span className="flex justify-center m-auto">
            <Icon size={6} color={IconColor.BLACK} id={IconID.TABLE_CONSISTENT} />
          </span>
        );
      case ChemicalResistance.partlyResistant:
        return (
          <span className="flex justify-center m-auto">
            <Icon size={6} color={IconColor.BLACK} id={IconID.TABLE_CONDITIONAL} />
          </span>
        );
      case ChemicalResistance.notResistant:
        return (
          <span className="flex justify-center m-auto">
            <Icon size={6} color={IconColor.BLACK} id={IconID.TABLE_INCONSISTENT} />
          </span>
        );
      default:
        return <span />;
    }
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => onResize(), [isFullScreen]);

  const table = (tableData: TableData, dataType: TableDataType) => (
    <table>
      <thead>
        <th className="min-w-[130px] md:min-w-[180px] sticky top-0 left-0 z-20 bg-white" />
        {materials.map((material, index) => (
          <th
            key={material.id}
            className={`min-w-[120px] px-2 whitespace-nowrap ${
              index % 2 === 0 ? 'bg-gray-50' : ''
            } sticky top-0 z-10 bg-white`}
          >
            <p className="text-sm font-bold pt-2">
              {t(`MATERIALS.${material.name}_NAME`, { ns: 'free-form-hub/materials' })}
            </p>
            {/* FIXME: do use helpers from ibh */}
            <p className="text-sm font-normal">
              {BearingHubHelper.getProductionMethodName(
                material.procedure as unknown as IbhMaterialResponseDtoProcedureEnum
              )}
            </p>
            <button
              type="button"
              className="uppercase text-igus-primary font-medium py-6"
              data-testid={`select_material_${material.id}`}
              onClick={() => {
                setValue([material.id]);
                setActiveModal(false);
                CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.filterCompareMaterialsOpen);
              }}
            >
              {t(`COMPARE_MATERIALS_MODAL.SELECT`, { ns: 'free-form-hub/freeFormHub' })}
            </button>
          </th>
        ))}
      </thead>
      <tbody>
        {tableData.map((data, dataIndex) => (
          <tr key={data.key} className="border-t border-[#AFAFAF]">
            <td className="px-4 text-sm sticky left-0 z-10 bg-white">
              {dataType === TableDataType.CHARACTERISTIC &&
                t(`COMPARE_MATERIALS_MODAL.${data.translationKey}`, { ns: 'free-form-hub/freeFormHub' })}
              {dataType === TableDataType.CHEMICAL &&
                t(`CHEMICALS.${data.translationKey}`, { ns: 'free-form-hub/chemicals' })}
            </td>
            {materials.map((material, index) => (
              <td key={material.id} className={`h-[56px] ${index % 2 === 0 ? 'bg-gray-50' : ''}`}>
                {dataType === TableDataType.CHARACTERISTIC &&
                  canRenderDot(material, data.key as keyof Omit<Material, 'chemicals'>) && (
                    <span
                      className="block w-[12px] h-[12px] bg-gray-800 rounded-full m-auto"
                      data-testid={`${material.id}_${data.key}_dot`}
                    />
                  )}
                {dataType === TableDataType.CHEMICAL && renderChemicalResistance(material, dataIndex)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const loadMoreChemicals = () => {
    const startIndex = (currentPage - 1) * chemicalsPerPage;
    const endIndex = startIndex + chemicalsPerPage;
    const newChemicals = presentedChemicals.slice(startIndex, endIndex);

    if (newChemicals.length > 0) {
      setLoadedChemicals(prevChemicals => [...prevChemicals, ...newChemicals]);
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 2) {
      loadMoreChemicals();
    }
  };

  const reset = () => {
    setTabIndex(0);
    setLoadedChemicals([]);
    setCurrentPage(1);
  };

  const tabs = [
    {
      title: t(`COMPARE_MATERIALS_MODAL.TAB_MATERIAL_CHARACTERISTICS`, { ns: 'free-form-hub/freeFormHub' }),
      content: (
        <div style={{ maxHeight: `${maxTableHeight - 20}px` }} className="overflow-auto h-full">
          {table(presentedMaterialCharacteristic, TableDataType.CHARACTERISTIC)}
        </div>
      ),
    },
    {
      title: t(`CHEMICALS.CHEMICALS`, { ns: 'free-form-hub/chemicals' }),
      content: (
        <div onScroll={handleScroll} style={{ maxHeight: `${maxTableHeight - 20}px` }} className="overflow-auto h-full">
          {table(loadedChemicals, TableDataType.CHEMICAL)}
        </div>
      ),
    },
  ];

  const chemicalProperties = [
    {
      key: 'RESISTANT',
      iconId: IconID.TABLE_CONSISTENT,
    },
    {
      key: 'LIMITED_RESISTANT',
      iconId: IconID.TABLE_CONDITIONAL,
    },
    {
      key: 'NOT_RESISTANT',
      iconId: IconID.TABLE_INCONSISTENT,
    },
  ];

  return (
    <Dialog
      visible={activeModal}
      closable
      fullScreen
      onClose={() => {
        setActiveModal(false);
        reset();
        CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.filterCompareMaterialsTabClose);
      }}
      onSetFullScreen={setIsFullScreen}
    >
      <div data-testid="dialog-wrapper" className={`${isFullScreen ? 'w-[100vw] h-[100vh]' : 'w-[50vw]'} pt-16 px-8`}>
        <h3>{t(`HEADLINES.COMPARE_MATERIALS_MODAL`, { ns: 'free-form-hub/freeFormHub' })}</h3>
        <p>
          {t(`COMPARE_MATERIALS_MODAL.DESCRIPTION`, {
            ns: 'free-form-hub/freeFormHub',
            quantity: materials.length,
          })}
        </p>
        {tabIndex === 1 && (
          <div className="lg:space-x-10 flex-col flex lg:flex-row mt-4 text-igus-primary">
            {chemicalProperties.map(property => (
              <div key={property.key} className="space-x-3 flex items-center">
                <span>
                  <Icon size={6} color={IconColor.BLACK} id={property.iconId} />
                </span>
                <span>{t(`CHEMICAL_PROPERTIES.${property.key}`, { ns: 'free-form-hub/chemicals' })}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mb-8">
          <Tabs
            items={tabs}
            onTabButtonClick={value => {
              CustomEvent.trackEvent(
                ffhEventCategory,
                TrackedFfhEventActions.filterCompareMaterialsTabSelect,
                value === 0 ? 'Material properties' : 'Chemicals'
              );
              setTabIndex(value);
              if (value === 0) {
                reset();
              } else {
                loadMoreChemicals();
              }
            }}
            headerWidth="auto"
          />
        </div>
      </div>
    </Dialog>
  );
};
